import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const IDCaptureModal = ({
  open,
  onClose,
  onSubmit,
  title,
  submitLabel,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);

  const [currentSide, setCurrentSide] = useState(null); // "front" | "back" | null
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [isCameraReady, setCameraReady] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const resetCameraState = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  const resetModalState = useCallback(() => {
    resetCameraState();
    setCurrentSide(null);
    setError("");
  }, [resetCameraState]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setFrontImage(null);
        setBackImage(null);
        resetModalState();
      }, 200);
    }
  }, [open, resetModalState]);

  useEffect(() => () => resetCameraState(), [resetCameraState]);

  const startCapture = useCallback(
    async (side) => {
      // Check if we're in a secure context (HTTPS or localhost)
      const isSecureContext = window.isSecureContext || 
                             window.location.protocol === 'https:' || 
                             window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1';
      
      if (!isSecureContext) {
        setError("Camera access requires a secure connection (HTTPS). Please access this application using HTTPS (https://) instead of HTTP (http://).");
        resetCameraState();
        setCurrentSide(null);
        return;
      }
      
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Camera access is not supported in this browser. Please use a modern browser.");
        resetCameraState();
        setCurrentSide(null);
        return;
      }
      
      try {
        setError("");
        resetCameraState();
        setCurrentSide(side);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
          },
          audio: false,
        });
        mediaStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {
            setError(
              "Unable to start the camera preview. Please check camera permissions."
            );
          });
        }
      } catch (cameraError) {
        let cameraErrorMessage = "Unable to access the camera. Please check permissions and try again.";
        
        if (cameraError && cameraError.message) {
          if (cameraError.message.includes('secure context') || cameraError.message.includes('HTTPS')) {
            cameraErrorMessage = "Camera access requires a secure connection (HTTPS). Please access this application using HTTPS (https://) instead of HTTP (http://).";
          } else {
            cameraErrorMessage = cameraError.message;
          }
        } else if (cameraError && cameraError.name) {
          if (cameraError.name === "NotAllowedError") {
            cameraErrorMessage = "Camera permission was denied. Please enable camera access in your browser settings.";
          } else if (cameraError.name === "NotFoundError") {
            cameraErrorMessage = "No camera found. Please connect a camera and try again.";
          }
        }
        
        setError(cameraErrorMessage);
        resetCameraState();
        setCurrentSide(null);
      }
    },
    [resetCameraState]
  );

  const handleVideoCanPlay = () => {
    setCameraReady(true);
  };

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      return null;
    }
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const aspectRatio = 1.586;
    const { videoWidth, videoHeight } = video;
    if (!videoWidth || !videoHeight) {
      return null;
    }

    let drawWidth = videoWidth;
    let drawHeight = drawWidth / aspectRatio;
    if (drawHeight > videoHeight) {
      drawHeight = videoHeight;
      drawWidth = drawHeight * aspectRatio;
    }
    const offsetX = (videoWidth - drawWidth) / 2;
    const offsetY = (videoHeight - drawHeight) / 2;

    canvas.width = drawWidth;
    canvas.height = drawHeight;
    context.drawImage(
      video,
      offsetX,
      offsetY,
      drawWidth,
      drawHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return canvas.toDataURL("image/jpeg", 0.92);
  }, []);

  const handleTakePhoto = () => {
    const dataUrl = captureFrame();
    if (!dataUrl) {
      setError("Could not capture image. Please try again.");
      return;
    }

    if (currentSide === "front") {
      setFrontImage(dataUrl);
    } else if (currentSide === "back") {
      setBackImage(dataUrl);
    }
    resetCameraState();
    setCurrentSide(null);
  };

  const handleCancelCapture = () => {
    resetCameraState();
    setCurrentSide(null);
  };

  const handleClose = () => {
    resetModalState();
    onClose && onClose();
  };

  const handleSubmit = async () => {
    if (!frontImage || !backImage || !onSubmit) {
      handleClose();
      return;
    }
    try {
      setSubmitting(true);
      await onSubmit(frontImage, backImage);
      handleClose();
    } catch (submitError) {
      const submitErrorMessage =
        (submitError && submitError.message) ||
        "Something went wrong while saving the captured ID. Please try again.";
      setError(submitErrorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const disabledSave = useMemo(
    () => !frontImage || !backImage || isSubmitting,
    [frontImage, backImage, isSubmitting]
  );

  const renderSelectionView = () => (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        py: 4,
        px: { xs: 2, md: 6 },
        overflowY: "auto",
      }}
    >
      <Typography
        variant="body1"
        sx={{ color: "#475569", textAlign: "center", maxWidth: "720px", mx: "auto" }}
      >
        Capture a clear image of both the front and back of the patient&rsquo;s ID.
        Ensure the text is readable and the entire ID is within the frame.
      </Typography>

      <Grid container spacing={3} justifyContent="center">
        {[{ side: "front", label: "Front of ID" }, { side: "back", label: "Back of ID" }].map(
          ({ side, label }) => {
            const imageValue = side === "front" ? frontImage : backImage;
            const isCaptured = Boolean(imageValue);
            return (
              <Grid item xs={12} md={5} key={side}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #e2e8f0",
                    borderRadius: 3,
                    boxShadow: "0 15px 40px -20px rgba(15, 23, 42, 0.3)",
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#1e293b" }}
                      >
                        {label}
                      </Typography>
                      {isCaptured && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "999px",
                            backgroundColor: "#dcfce7",
                            color: "#166534",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        >
                          <CheckCircleIcon fontSize="small" />
                          Captured
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        height: 220,
                        borderRadius: 2,
                        border: "2px dashed #cbd5e1",
                        backgroundColor: "#f8fafc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {isCaptured ? (
                        <Box
                          component="img"
                          src={imageValue}
                          alt={`${label} preview`}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <PhotoCameraIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 3,
                      pb: 3,
                    }}
                  >
                    {isCaptured ? (
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ReplayIcon />}
                        onClick={() => startCapture(side)}
                        sx={{
                          textTransform: "none",
                          borderRadius: 2,
                          px: 2.5,
                        }}
                      >
                        Retake
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<PhotoCameraIcon />}
                        onClick={() => startCapture(side)}
                        sx={{
                          textTransform: "none",
                          borderRadius: 2,
                          px: 2.5,
                          backgroundColor: "#7c3aed",
                          "&:hover": {
                            backgroundColor: "#6d28d9",
                          },
                        }}
                      >
                        Capture
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          }
        )}
      </Grid>
    </Box>
  );

  const renderCameraView = () => (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        px: { xs: 2, md: 6 },
        gap: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "#1e293b", textAlign: "center" }}
      >
        Align the {currentSide === "front" ? "front" : "back"} of the ID inside the frame
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 720,
          aspectRatio: "1.586 / 1",
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          onCanPlay={handleVideoCanPlay}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {!isCameraReady && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(15, 23, 42, 0.6)",
            }}
          >
            <CircularProgress size={48} sx={{ color: "#ffffff" }} />
          </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            inset: 16,
            borderRadius: 3,
            border: "3px solid rgba(124, 58, 237, 0.7)",
            pointerEvents: "none",
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancelCapture}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3,
          }}
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isCameraReady}
          onClick={handleTakePhoto}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3,
            backgroundColor: "#7c3aed",
            "&:hover": {
              backgroundColor: "#6d28d9",
            },
          }}
          startIcon={<PhotoCameraIcon />}
        >
          Take Photo
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
            px: { xs: 2, md: 3 },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <IconButton
            edge="end"
            onClick={handleClose}
            sx={{
              color: "#ffffff",
              "&:hover": { color: "#e0e7ff" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            background:
              "linear-gradient(180deg, #f8fafc 0%, rgba(248, 250, 252, 0.6) 100%)",
          }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{ borderRadius: 0, borderBottom: "1px solid #fecaca" }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: { xs: "auto", md: "70vh" },
            }}
          >
            {currentSide ? renderCameraView() : renderSelectionView()}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            px: { xs: 2, md: 4 },
            py: 2.5,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={disabledSave}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3.5,
              backgroundColor: "#7c3aed",
              "&:hover": {
                backgroundColor: "#6d28d9",
              },
            }}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {submitLabel}
          </Button>
        </DialogActions>

        <canvas ref={canvasRef} style={{ display: "none" }} />
      </Dialog>
  );
};

IDCaptureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
};

IDCaptureModal.defaultProps = {
  onClose: undefined,
  onSubmit: undefined,
  title: "ID Verification",
  submitLabel: "Submit ID",
};

export default IDCaptureModal;





// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/IDCaptureModal.jsx